/* screens/academy/OrdersTab.jsx — вкладка «Заказы» консоли Академии (#10).
   МОП/методист видит заказы курсов (по умолчанию НЕОПЛАЧЕННЫЕ) с контактами ученика,
   чтобы дожать оплату. Данные — GET /api/academy/manage/orders/ (academy.getOrders).
   Контракт ответа (orders-list.json): { count, results:[{ id, status, amount_rub,
   created_at, paid_at, course:{id,title}, tariff:{id,title}|null,
   user:{id,name,email,phone}, consents:{offer,privacy,reviews,mailings}|null,
   upgrade:{from_tariff_id,from_tariff_title,from_price_rub,target_price_rub}|null }] }.
   Гейт правом academy_manage делает родитель (AcademyConsole). Таблица — cv-table
   (row-as-card на мобиле). Без raw HTML; ссылки только mailto:/tel:.

   PL-12: `upgrade` — это ДРУГАЯ денежная операция (доплата до старшего тарифа,
   списывается разница цен). Колонка «Операция» есть у КАЖДОЙ строки — у продажи
   тоже: сверять деньги по таблице, где особые строки отличаются лишь отсутствием
   подписи, нельзя. */
import React from 'react';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { SearchInput, Field, TextInput, Textarea } from '../../components/ui/Field.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import * as api from '../../lib/api/academy.js';
import { TabScroller } from './TabScroller.jsx';
import { writeClipboard } from './clipboard.js';

const { useState, useCallback } = React;
const { useFakeQuery } = hooks;

const STATUS_FILTERS = [
  { value: 'pending', label: 'Не оплачены' },
  { value: 'succeeded', label: 'Оплаченные' },
  { value: 'failed', label: 'С ошибкой' },
  { value: 'cancelled', label: 'Отменённые' },
  { value: 'all', label: 'Все' },
];

const STATUS_TONE = { pending: 'warn', succeeded: 'ok', failed: 'bad', cancelled: 'neutral' };
const STATUS_LABEL = { pending: 'Не оплачен', succeeded: 'Оплачен', failed: 'Ошибка', cancelled: 'Отменён' };

// согласия: оферта/политика — обязательные, отзывы/рассылки — опциональные (#9)
const CONSENT_META = [
  { key: 'offer', label: 'Оферта' },
  { key: 'privacy', label: 'Политика' },
  { key: 'reviews', label: 'Отзывы' },
  { key: 'mailings', label: 'Рассылки' },
];

function fmtDateTime(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (isNaN(dt)) return '—';
  return dt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// amount_rub приходит строкой-decimal — форматируем как деньги, без потери копеек.
function fmtAmount(raw) {
  if (raw == null || raw === '') return '—';
  const n = Number(raw);
  if (isNaN(n)) return String(raw);
  const s = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(n);
  return `${s} ₽`;
}

/* PL-12. Как строка заказа НАЗЫВАЕТ свою денежную операцию.
   Продажа тарифа и доплата до старшего тарифа списывают разные суммы за разное:
   у доплаты в «Сумме» стоит РАЗНИЦА цен, а в «Тарифе» — тариф, ДО которого
   доплачивают. Без подписи бухгалтер читал «Тариф М · 3 000 ₽» как продажу тарифа
   М за 3 000 ₽ и получал расхождение с выпиской на цену базового тарифа.
   Чистая функция (без JSX) — она под юнит-тестом: текст в деньгах важнее вида. */
export function orderOperation(order) {
  const up = (order && order.upgrade) || null;
  if (!up) {
    return { kind: 'purchase', label: 'Продажа', tone: 'neutral',
      tariffNote: '', amountNote: '' };
  }
  const from = up.from_tariff_title ? `«${up.from_tariff_title}»` : 'прежнего тарифа';
  const fromPrice = up.from_price_rub != null && up.from_price_rub !== ''
    ? ` · ${fmtAmount(up.from_price_rub)}` : '';
  return {
    kind: 'upgrade',
    label: 'Доплата',
    tone: 'info',
    tariffNote: `доплата с ${from}${fromPrice}`,
    // Полная цена целевого тарифа обязана стоять рядом с суммой: иначе разницу
    // и читают как цену тарифа. Нет её в meta — говорим словами, что это разница.
    amountNote: up.target_price_rub != null && up.target_price_rub !== ''
      ? `разница · тариф целиком ${fmtAmount(up.target_price_rub)}`
      : 'разница цен, а не полная цена тарифа',
  };
}

// компактный индикатор согласий: ✓ данное / ✗ нет. consents может быть null (старые заказы).
function ConsentMarks({ consents }) {
  if (!consents) return <span className="text-mute">—</span>;
  return (
    <span className="ord-consents" role="group" aria-label="Согласия">
      {CONSENT_META.map((c) => {
        const on = !!consents[c.key];
        return (
          <span key={c.key} className={'ord-consent' + (on ? ' is-on' : '')}
            title={`${c.label}: ${on ? 'дано' : 'нет'}`}>
            <Icon name={on ? 'check' : 'close'} size={11} aria-hidden="true" />
            <span className="ord-consent__lbl">{c.label}</span>
          </span>
        );
      })}
    </span>
  );
}

/* M3. «Отметить оплаченным» открывает платный доступ мимо провайдера — это
   ДЕНЕЖНЫЙ путь, и он обязан оставлять след: бэк кладёт основание в meta заказа
   (`manual_paid_comment` / `manual_paid_invoice_no` рядом с `manual_paid_by/at`).
   Раньше консоль звала markOrderPaid(id) без второго аргумента, и при сверке
   денег в заказе оставалось только «кто-то из методистов открыл доступ».
   Обязательное поле причины — по образцу переоткрытия проверки в ReviewDrawer:
   диалог подтверждения (ConfirmProvider) полей ввода не умеет, поэтому свой
   Modal, а не confirm(). */
function MarkPaidDialog({ order, busy, onCancel, onConfirm }) {
  const [comment, setComment] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [error, setError] = useState('');
  const u = order.user || {};
  const submit = () => {
    const c = comment.trim();
    if (!c) { setError('Укажите основание — оно попадёт в журнал заказа'); return; }
    onConfirm({ comment: c, invoiceNo: invoiceNo.trim() });
  };
  return (
    <Modal open size="sm" onClose={onCancel} title="Отметить оплаченным вручную"
      footer={<>
        <Button variant="ghost" onClick={onCancel}>Отмена</Button>
        <Button variant="primary" loading={busy} onClick={submit}>Отметить оплаченным</Button>
      </>}>
      <div className="stack stack-4">
        <div className="t-body text-dim">
          Так закрывают оплату по счёту мимо платёжного провайдера: {u.name || u.email || 'ученику'}
          {' '}откроется доступ к курсу «{order.course?.title || '—'}», как после обычной оплаты.
          Деньги при этом не списываются.
        </div>
        <Field label="Основание" error={error}
          hint="Кто и на каком основании открыл доступ — это единственный след операции при сверке денег.">
          <Textarea value={comment} minRows={2} invalid={!!error}
            onChange={(e) => { setComment(e.target.value); if (error) setError(''); }}
            placeholder="Например: оплата по счёту №142 от 05.08, поступила на р/с" />
        </Field>
        <Field label="Номер счёта" hint="необязательно · попадёт в заказ отдельным полем">
          <TextInput value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
            placeholder="142" />
        </Field>
      </div>
    </Modal>
  );
}

/* AD-11 + мелочь QA. Перевыставленную ссылку печатали в тост с автозакрытием на
   12 секунд, хотя смысл действия — ОТПРАВИТЬ ссылку покупателю: из исчезающего
   тоста её не выделить и не скопировать. Ссылка живёт в модалке, пока методист
   сам её не закроет: копирование, письмо покупателю и видимый адрес.
   Модалку рендерит OrdersTab, а НЕ строка заказа: см. комментарий у reissue(). */
function ReissueLinkDialog({ order, link, onClose }) {
  const toast = useToast();
  const u = order.user || {};
  const copy = async () => {
    const ok = await writeClipboard(link);
    toast.show(ok ? 'Ссылка скопирована' : 'Скопировать не удалось — выделите адрес вручную',
      ok ? 'success' : 'muted');
  };
  const mailto = u.email
    ? `mailto:${u.email}?subject=${encodeURIComponent('Оплата курса «' + (order.course?.title || '') + '»')}`
      + `&body=${encodeURIComponent('Ссылка на оплату: ' + link)}`
    : null;
  return (
    <Modal open size="sm" onClose={onClose} title="Ссылка на оплату перевыставлена"
      footer={<Button variant="primary" onClick={onClose}>Готово</Button>}>
      <div className="stack stack-4">
        <div className="t-body text-dim">
          Отправьте её покупателю. Старая ссылка больше не действует.
        </div>
        {/* readOnly, а не disabled: адрес должен выделяться мышью и клавиатурой,
            если буфер обмена недоступен (не-https, старый webview). */}
        <input className="input" readOnly value={link} aria-label="Ссылка на оплату"
          onFocus={(e) => e.target.select()} />
        <div className="cluster cluster-8" style={{ flexWrap: 'wrap' }}>
          <Button size="sm" variant="secondary" iconLeft="copy" onClick={copy}>Копировать</Button>
          {mailto && (
            <Button size="sm" variant="ghost" iconLeft="mail"
              onClick={() => { window.location.href = mailto; }}>Письмом на {u.email}</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* AD-11. Действия в строке заказа. Раньше вкладка была витриной: перевыставить
   ссылку, отменить или отметить оплату по счёту было нечем, а фильтр «Отменённые»
   стоял вечно пустым, потому что статус cancelled никто не выставлял. */
function OrderActions({ order, onChanged, onReissued }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState('');
  const [paidOpen, setPaidOpen] = useState(false);
  // Все три действия бэк разрешает ТОЛЬКО по висящему заказу (OrderActionView:
  // not_pending). Показывать их на оплаченном или отменённом значило бы предлагать
  // кнопку, которая гарантированно вернёт ошибку.
  const pending = order.status === 'pending';

  const run = useCallback(async (kind, fn) => {
    setBusy(kind);
    try { return await fn(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Не удалось выполнить действие', 'danger'); return null; }
    finally { setBusy(''); }
  }, [toast]);

  // Список перечитываем только после действий, которые правда меняют строку
  // (см. api.orderActionNeedsReload — там же цена ошибки).
  const finish = useCallback((action) => {
    if (api.orderActionNeedsReload(action)) onChanged();
  }, [onChanged]);

  const reissue = async () => {
    const r = await run('reissue', () => api.reissueOrder(order.id));
    if (!r) return;
    /* Ссылку показываем, а не открываем: её отправляют покупателю, а не проходят
       сами. Состояние модалки ПОДНЯТО в OrdersTab: раньше адрес лежал в стейте
       этой строки, а следом в том же тике шёл рефетч — QueryBoundary на время
       загрузки снимал таблицу вместе с OrderActions, и окно со ссылкой умирало
       через кадр после появления. Человек не видел ни окна, ни тоста, а ссылка,
       только что выпущенная для клиента, пропадала безвозвратно. */
    if (r.redirect_url) onReissued(order, r.redirect_url);
    else toast.show('Ссылка перевыставлена, но адрес не пришёл — обновите список', 'warning');
    finish('reissue');
  };
  const cancel = async () => {
    if (!await confirm({ title: 'Отменить заказ?',
      body: 'Заказ уйдёт в «Отменённые». Если покупатель вернётся к оплате, он создаст новый заказ.',
      confirmLabel: 'Отменить заказ', danger: true })) return;
    if (await run('cancel', () => api.cancelOrder(order.id))) {
      toast.show('Заказ отменён', 'muted'); finish('cancel');
    }
  };
  const markPaid = async (basis) => {
    if (await run('paid', () => api.markOrderPaid(order.id, basis))) {
      setPaidOpen(false);
      toast.show('Заказ отмечен оплаченным — доступ к курсу открыт', 'success');
      finish('mark-paid');
    }
  };

  if (!pending) return <span className="text-mute">—</span>;
  return (
    <span className="ord-actions">
      <Button size="sm" variant="ghost" iconLeft="link" loading={busy === 'reissue'}
        onClick={reissue}>Перевыставить</Button>
      <Button size="sm" variant="ghost" iconLeft="check" loading={busy === 'paid'}
        onClick={() => setPaidOpen(true)}>Оплачен</Button>
      <Button size="sm" variant="ghost" iconLeft="close" loading={busy === 'cancel'}
        onClick={cancel}>Отменить</Button>
      {paidOpen && (
        <MarkPaidDialog order={order} busy={busy === 'paid'}
          onCancel={() => setPaidOpen(false)} onConfirm={markPaid} />
      )}
    </span>
  );
}

function OrdersTab() {
  const [status, setStatus] = useState('pending');
  const [course, setCourse] = useState('');
  const [q, setQ] = useState('');
  const [nonce, setNonce] = useState(0);
  /* AD-11. Перевыставленная ссылка живёт ЗДЕСЬ, а не в строке заказа: строка
     висит внутри QueryBoundary и исчезает на любой перезагрузке списка вместе со
     своим стейтом. {order, link}; null — окно закрыто. */
  const [reissued, setReissued] = useState(null);
  const onReissued = useCallback((order, link) => setReissued({ order, link }), []);

  const coursesQ = useFakeQuery('acad-orders-courses', () => api.listCourses(), []);
  const courses = coursesQ.data || [];

  const key = [status, course, q, nonce].join('|');
  const ordersQ = useFakeQuery('acad-orders-' + key,
    () => api.getOrders({
      // status=all → не шлём параметр (бэк отдаёт все); пустые — опускаем
      status: status === 'all' ? '' : status,
      course_id: course,
      q,
    }), [key]);
  const reloadOrders = useCallback(() => setNonce((n) => n + 1), []);

  const data = ordersQ.data || {};
  const rows = Array.isArray(data.results) ? data.results : [];
  const count = typeof data.count === 'number' ? data.count : rows.length;
  const hasFilters = !!course || !!q || status !== 'pending';
  const reset = () => { setStatus('pending'); setCourse(''); setQ(''); };

  return (
    <>
      <TabScroller>
        <div className="acad-tabs" role="tablist" aria-label="Статус заказа">
          {STATUS_FILTERS.map((f) => (
            <button key={f.value} type="button" role="tab" aria-selected={status === f.value}
              className={'acad-seg' + (status === f.value ? ' is-active' : '')}
              onClick={() => setStatus(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
      </TabScroller>

      <div className="acad-filters">
        <label className="acad-filter">
          <span className="acad-filter__lbl">Курс</span>
          <select className="input" value={course} onChange={(e) => setCourse(e.target.value)}>
            <option value="">Все курсы</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </label>
        <label className="acad-filter ord-search">
          <span className="acad-filter__lbl">Поиск по контакту</span>
          <SearchInput value={q} onDebouncedChange={setQ} placeholder="Email, имя или телефон" />
        </label>
        {hasFilters && <Button variant="ghost" size="sm" iconLeft="close" onClick={reset}>Сбросить</Button>}
      </div>

      <QueryBoundary loading={ordersQ.loading} error={ordersQ.error} empty={!rows.length} onRetry={ordersQ.reload}
        forbiddenBody="У вас нет прав на просмотр заказов Академии."
        emptyEl={hasFilters
          ? <EmptyState serif={false} icon="search" title="Заказов нет"
              body="По выбранным фильтрам заказов не найдено. Сбросьте фильтры, чтобы увидеть все."
              action={<Button variant="secondary" iconLeft="close" onClick={reset}>Сбросить фильтры</Button>} />
          : <EmptyState icon="credit-card" title="Заказов нет"
              body="Здесь появятся заказы курсов. Неоплаченные показаны первыми — с контактами ученика для связи." />}>
        <div className="ord-count t-caption text-mute">Всего заказов: <span className="num">{count}</span></div>
        <div className="cv-table-wrap">
          <table className="cv-table ord-table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th>Курс</th>
                <th>Тариф</th>
                <th>Операция</th>
                <th className="num">Сумма</th>
                <th>Статус</th>
                <th>Создан</th>
                <th>Оплачен</th>
                <th>Согласия</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => {
                const u = o.user || {};
                const name = u.name || u.email || 'Без имени';
                const op = orderOperation(o);
                return (
                  <tr key={o.id} className="ord-row">
                    <td data-label="Ученик">
                      <span className="cv-strong">{name}</span>
                      <span className="ord-contacts">
                        {u.email && (
                          <a className="ord-contact" href={`mailto:${u.email}`}>
                            <Icon name="mail" size={13} aria-hidden="true" />{u.email}
                          </a>
                        )}
                        {/* «phone» в реестре Icon нет — глиф молча рисовался пустым.
                            Телефон подписан тем же значком, что в карточке ученика. */}
                        {u.phone && (
                          <a className="ord-contact" href={`tel:${u.phone}`}>
                            <Icon name="user" size={13} aria-hidden="true" />{u.phone}
                          </a>
                        )}
                      </span>
                    </td>
                    <td data-label="Курс">{o.course?.title || '—'}</td>
                    <td data-label="Тариф" className="text-dim">
                      {/* Обёртка `ord-cell` обязательна: на мобиле ячейка cv-table —
                          flex-строка «подпись ↔ значение», и две отдельные span'а
                          разъехались бы по ней третьим элементом. */}
                      <span className="ord-cell">
                        <span>{o.tariff?.title || '—'}</span>
                        {op.tariffNote && <span className="ord-cell__note">{op.tariffNote}</span>}
                      </span>
                    </td>
                    <td data-label="Операция">
                      <Pill tone={op.tone} size="sm" glyph={false}>{op.label}</Pill>
                    </td>
                    <td data-label="Сумма" className="num">
                      <span className="ord-cell">
                        <span>{fmtAmount(o.amount_rub)}</span>
                        {op.amountNote && <span className="ord-cell__note">{op.amountNote}</span>}
                      </span>
                    </td>
                    <td data-label="Статус">
                      <Pill tone={STATUS_TONE[o.status] || 'neutral'} size="sm" glyph={false}>
                        {STATUS_LABEL[o.status] || o.status}
                      </Pill>
                    </td>
                    <td data-label="Создан" className="num">{fmtDateTime(o.created_at)}</td>
                    <td data-label="Оплачен" className="num">{fmtDateTime(o.paid_at)}</td>
                    <td data-label="Согласия"><ConsentMarks consents={o.consents} /></td>
                    <td data-label="Действия">
                      <OrderActions order={o} onChanged={reloadOrders} onReissued={onReissued} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </QueryBoundary>

      {/* Вне QueryBoundary намеренно: окно со ссылкой обязано пережить и рефетч
          списка, и смену фильтров — иначе адрес теряется вместе с таблицей. */}
      {reissued && (
        <ReissueLinkDialog order={reissued.order} link={reissued.link}
          onClose={() => setReissued(null)} />
      )}
    </>
  );
}

export { OrdersTab };
